/**
 * Product Grid.
 * @element product-grid
 */
if (!customElements.get('product-grid')) {
  customElements.define(
    'product-grid',
    class ProductGrid extends HTMLElement {
      constructor() {
        super();
        this.loadMoreButton = this.querySelector('[data-load-more]');
        this.loadPreviousButton = this.querySelector('[data-load-previous]');
        this.collectionList = this.querySelector('[data-collection-list]');
        this.productCount = this.querySelector('[data-product-count]');

        window.ProductGrid = ProductGrid;
      }

      connectedCallback() {
        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', this.loadNext.bind(this));
        }

        if (this.loadPreviousButton) {
          this.loadPreviousButton.addEventListener('click', this.loadPrevious.bind(this));
        }

        // show previous button if there is a previous page
        if (this.dataset.previousPageUrl.length > 0) {
          this.loadPreviousButton.parentElement.classList.remove('hidden');
        }
      }

      /**
       * Loads the next page of products and appends them to the collection list.
       */
      async loadNext() {
        this.nextPageUrl = this.dataset.nextPageUrl;
        this.toggleLoading();
        const productsHtml = await this.renderPage(this.nextPageUrl);
        const products = this.getProducts(productsHtml);
        this.appendProducts(products);
        this.updateUrl(this.nextPageUrl);
        this.updateData(productsHtml);
        this.toggleLoading();
        if (this.dataset.nextPageUrl.length === 0) {
          this.loadMoreButton.classList.add('hidden');
        }
      }

      /**
       * Loads the previous page of products and prepends them to the collection list.
       */
      async loadPrevious() {
        this.previousPageUrl = this.dataset.previousPageUrl;
        this.toggleLoading();
        const productsHtml = await this.renderPage(this.previousPageUrl);
        const products = this.getProducts(productsHtml);
        this.prependProducts(products);
        this.updateUrl(this.previousPageUrl);
        this.updateData(productsHtml);
        this.toggleLoading();
        if (this.dataset.previousPageUrl.length === 0) {
          this.loadPreviousButton.parentElement.classList.add('hidden');
        }
      }

      /**
       * Renders the page at the given URL and returns the HTML content.
       * @param {string} url - The URL of the page to render.
       * @returns {Promise<string>} - The HTML content of the rendered page.
       */
      async renderPage(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.statusText);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const mainProductGrid = doc.querySelector('#main-collection-product-grid');
        return mainProductGrid ? mainProductGrid.innerHTML : '';
      }

      /**
       * Parses the HTML content and returns an array of product elements.
       * @param {string} productsHtml - The HTML content to parse.
       * @returns {HTMLElement[]} - An array of product elements.
       */
      getProducts(productsHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(productsHtml, 'text/html');
        const productElements = doc.querySelectorAll('li');
        return Array.from(productElements);
      }

      /**
       * Updates the URL in the browser history.
       * @param {string} url - The URL to update to.
       */
      updateUrl(url) {
        history.pushState('', '', url);
      }

      /**
       * Prepends the given products to the collection list.
       * @param {HTMLElement[]} products - The products to prepend.
       */
      prependProducts(products) {
        products.forEach((product) => this.collectionList.prepend(product));
      }

      /**
       * Appends the given products to the collection list.
       * @param {HTMLElement[]} products - The products to append.
       */
      appendProducts(products) {
        products.forEach((product) => this.collectionList.appendChild(product));
      }

      /**
       * Updates the dataset with the next and previous page URLs.
       * @param {string} productsHtml - The HTML content to parse.
       */
      updateData(productsHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(productsHtml, 'text/html');
        const nextPageUrl = doc.querySelector('[data-next-page-url]');
        const previousPageUrl = doc.querySelector('[data-previous-page-url]');
        this.setAttribute('data-next-page-url', nextPageUrl.dataset.nextPageUrl);
        this.setAttribute('data-previous-page-url', previousPageUrl.dataset.previousPageUrl);
        this.updateProductCount();
      }

      /**
       * Toggles the loading state of the load more and load previous buttons.
       */
      toggleLoading() {
        if (this.loadMoreButton) {
          this.loadMoreButton.classList.toggle('button--loading');
          this.loadMoreButton.blur();
        }
        if (this.loadPreviousButton) {
          this.loadPreviousButton.classList.toggle('button--loading');
          this.loadPreviousButton.blur();
        }
      }

      updateProductCount() {
        this.productCount.innerText = this.collectionList.querySelectorAll('li').length;
      }
    }
  );
}
